import { ErrorBoundary } from "@sentry/react"
import { useMenuState } from "@szhsin/react-menu"
import { useQuery } from "@tanstack/react-query"
import api from "api"
import { queries } from "api/queries"
import { UserOrganizations } from "api/resources/growthspace/types"
import { GenderMapping, Profile } from "api/resources/profile/types"
import { Email, UserPreference } from "api/resources/settings/types"
import { OrganisationSlug } from "B2B/SuraasaSure/constants"
import ErrorPage from "components/ErrorPage"
import FullPageLoading from "components/FullPageLoading"
import { ResumeProfileFillerProvider } from "features/AItools/components/Generator/ResumeProfileFiller/Provider"
import { AuthManager } from "features/Authentication/AuthManager"
import { useITOAccess } from "features/ITO/useITOAccess"
import { isPgCTLProduct } from "features/LearningItems/getProductDisplayConfig"
import FeatureToggleManagerProvider from "global/FeatureToggleProvider"
import SuraasaSureDownloadPrompt from "growthspace/DownloadApp/SuraasaSureDownloadPrompt"
import { useEffect, useState } from "react"
import {
  Outlet,
  ScrollRestoration,
  useMatch,
  useNavigate,
} from "react-router-dom"
import routes from "routes"
import {
  BROWSER_STORAGE_KEYS,
  learningItemSlugs,
  PLATFORM_SETTINGS_SLUGS,
  Platforms,
} from "utils/constants"
import { getPlatformURL } from "utils/helpers"
import { isLegacyGrowthspace } from "utils/legacyGrowthspaces"
import { getCurrentPlatform } from "utils/platform"
import { trackingService } from "utils/tracking"

import { ValueOf } from "../../utils/types"
import { context, defaultContextValue } from "./context"

const GlobalContext = () => {
  const notificationsPopupMenuState = useMenuState({
    transition: true,
  })
  const [authInfo, setAuthInfo] = useState(AuthManager.getPlatformAuth())
  const [reMountingApp, setRemountingApp] = useState(false)

  const navigate = useNavigate()

  const isAuthenticated = !!authInfo
  const itoParticipationDetails = useITOAccess({ isAuthenticated })

  const [emails, setEmails] = useState<Email[]>(defaultContextValue.emails)

  const learningRestrictions = useQuery({
    enabled: isAuthenticated,
    queryKey: queries.learningItems.learningRestrictions().queryKey,
    queryFn: () => api.learningItems.listRestrictions(),
  })

  const currentPlatform = getCurrentPlatform()

  const listGrowthspaceQuery = useQuery({
    enabled: isAuthenticated && currentPlatform === Platforms.student,
    queryKey: queries.growthspace.list().queryKey,
    queryFn: async (): Promise<UserOrganizations[]> => {
      try {
        const data = await api.growthspace.list()

        localStorage.setItem(
          BROWSER_STORAGE_KEYS.userOrganizations,
          JSON.stringify(data)
        )
        return data
      } catch (error) {
        const cached = localStorage.getItem(
          BROWSER_STORAGE_KEYS.userOrganizations
        )
        if (cached) return JSON.parse(cached)

        throw error
      }
    },
    refetchOnWindowFocus: false,
  })

  const growthspaces = listGrowthspaceQuery.data

  const currentGrowthspace =
    growthspaces?.find(g => g.isCurrent) ??
    growthspaces?.find(g => g.isDefault) ??
    growthspaces?.[0] ??
    null

  const switchGrowthspace = async (orgId?: string) => {
    const url = new URL(getPlatformURL("sso", `/space/login/${orgId}`))
    url.searchParams.set("platform", Platforms.student)

    const currentURL = new URL(routes.home, window.location.origin)

    url.searchParams.set("redirect-url", currentURL.toString())

    window.location.href = url.toString()
  }

  // const dueAssignmentList = useQuery({
  //   enabled: isAuthenticated,
  //   queryKey: queries.assignments.dueAssignmentList().queryKey,
  //   queryFn: () => api.assignments.listDueAssignments({ params: {} }),
  // })

  const { data: userPreference } = useQuery({
    enabled: isAuthenticated,
    queryKey: [isAuthenticated],
    queryFn: () => api.settings.getUserPreferences(),
  })

  const platformSettings = userPreference
    ? (() => {
        try {
          if (!userPreference || userPreference?.length == 0) {
            return null
          }
          const preferences = userPreference.filter(x =>
            Object.values(PLATFORM_SETTINGS_SLUGS).includes(
              x.setting.slug as unknown as ValueOf<
                typeof PLATFORM_SETTINGS_SLUGS
              >
            )
          )
          const settings: { [x: string]: UserPreference } = {}
          preferences.forEach(v => {
            settings[v.setting.slug as string] = v
          })
          return settings
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
        } catch (error) {
          return null
        }
      })()
    : null

  const profileAPI = useQuery({
    enabled: isAuthenticated,
    queryKey: queries.profile.profileData().queryKey,
    queryFn: () =>
      api.profile.retrieve({
        urlParams: {},
      }),
    initialData: defaultContextValue.profile,
  })

  const emailsAPI = useQuery({
    enabled: isAuthenticated,
    queryKey: queries.settings.emailList().queryKey,
    queryFn: () => api.settings.emails.list(),
    onSuccess: data => setEmails(data),
  })

  const offerLettersQuery = useQuery({
    enabled: isAuthenticated,
    queryKey: queries.offerLetter.list().queryKey,
    queryFn: () => api.offerLetter.list(),
    select: data => data.filter(x => isPgCTLProduct(x.product.slug))[0],
  })

  const enrollments = useQuery({
    enabled: isAuthenticated,
    queryKey: queries.learningItems.enrollmentList().queryKey,
    queryFn: () => api.learningItems.listEnrollments(),
    onSuccess: data => {
      // $set, not $set_once: enrolling later must flip these to true
      trackingService.setPostHogUserAttribute({
        has_pgctl: Boolean(data.find(x => x.slug === learningItemSlugs.pgctl)),
        has_med: Boolean(data.find(x => x.slug === learningItemSlugs.med)),
      })
    },
  })

  const socialAccounts = useQuery({
    enabled: isAuthenticated,
    queryFn: () => api.auth.socialAuth.list(),
    queryKey: queries.settings.socialAccountsList().queryKey,
  })

  // useEffect(() => {
  //   const currentStage: ResumeFillerStageType = UserPreferenceManager.get(
  //     BROWSER_STORAGE_KEYS.resumeFillerStage
  //   )
  //   if (currentStage) {
  //     setResumeFillerStage(currentStage)
  //   } else {
  //     setResumeFillerStage(null)
  //   }
  // }, [])

  useEffect(() => {
    if (reMountingApp) {
      profileAPI.refetch()
      emailsAPI.refetch()
      enrollments.refetch()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reMountingApp])

  useEffect(() => {
    const profile = profileAPI.data
    if (profile && profile.id) {
      if (profile.phoneNumber) {
        trackingService.setUserAttribute(
          "mobile",
          `+${profile.phoneNumber.code}${profile.phoneNumber.number}`
        )
      }
      if (profile.country) {
        trackingService.setUserAttribute("COUNTRY", profile.country.name)
      }
      if (profile.state) {
        trackingService.setUserAttribute("STATE", profile.state.name)
      }
      if (profile.gender) {
        trackingService.setUserAttribute(
          "GENDER",
          GenderMapping[profile.gender]
        )
      }
      trackingService.setUserAttribute(
        "looking_for_jobs",
        profile.lookingForJobs
      )
      trackingService.setUserAttribute("is_verified", profile.isVerified)
    }
  }, [profileAPI.data])

  useEffect(() => {
    if (itoParticipationDetails.registrationStatus === "completed") {
      trackingService.setUserAttribute("ito_2023_registrant", true)
    }
  }, [itoParticipationDetails.registrationStatus])

  const isPGCTLUser = enrollments.data
    ? enrollments.data.length > 0
      ? enrollments.data.some(item => item.slug === learningItemSlugs.pgctl)
      : false
    : undefined

  const isGrowthSpaceUser =
    growthspaces && growthspaces.length > 0
      ? growthspaces[0].organization !== null
      : false

  useEffect(() => {
    trackingService.setPostHogUserAttribute({
      last_accessed_growthspace: currentGrowthspace?.organization
        ? "school"
        : "personal",
    })
  }, [currentGrowthspace])

  useEffect(() => {
    if (isPGCTLUser) {
      trackingService.setUserAttribute("is_pgctl_user", isPGCTLUser)
    }
  }, [isPGCTLUser])

  const remountApp = () => {
    if (reMountingApp) return

    setRemountingApp(true)

    setTimeout(() => {
      setRemountingApp(false)
    }, 150)
  }
  const currentGrowthSpaces = growthspaces?.find(item => item.isCurrent)
  const isNcte =
    currentGrowthSpaces?.organization?.slug === OrganisationSlug.ncte.slug ||
    currentGrowthSpaces?.organization?.id === OrganisationSlug.ncte.id

  const isCPATPaymentPage = useMatch(routes.cpat.paymentWithoutSignin)
  const isEmbeddedPaymentPage = useMatch(routes.embeddedPayment)
  const isLogoutPage = useMatch(routes.logout)

  const isSuraasaSure = !!currentGrowthspace?.organization
  const showDownloadPrompt =
    isSuraasaSure &&
    !isEmbeddedPaymentPage &&
    !isLogoutPage &&
    !isLegacyGrowthspace(currentGrowthspace?.organization?.slug) &&
    !currentGrowthspace?.paymentDetails?.paymentRequired

  useEffect(() => {
    if (
      currentGrowthspace?.paymentDetails?.paymentRequired &&
      !isEmbeddedPaymentPage
    )
      navigate(routes.embeddedPayment)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentGrowthspace?.paymentDetails?.paymentRequired,
    isEmbeddedPaymentPage,
  ])

  if (!isCPATPaymentPage && listGrowthspaceQuery.isLoading && isAuthenticated) {
    return <FullPageLoading debugMessage="Loading Growthspaces" />
  }

  return (
    <ErrorBoundary fallback={e => <ErrorPage data={e} />} showDialog>
      <context.Provider
        value={{
          notificationsPopupMenuState,
          itoParticipationDetails,
          authInfo,
          setAuthInfo: (newAuth, options) => {
            if (
              authInfo &&
              newAuth &&
              authInfo.user.uuid !== newAuth.user.uuid &&
              !options?.skipRemount
            ) {
              remountApp()
            }

            AuthManager.setPlatformAuth(newAuth)

            if (!options?.skipRemount) {
              setAuthInfo(newAuth)
            }
          },
          isAuthenticated,
          emails,
          enrollments,
          updateEmails: emails => setEmails(emails),
          profile: profileAPI.data as Profile,
          fetchProfile: profileAPI.refetch,
          learningRestrictions: learningRestrictions.data,
          platformSettings,
          // dueAssignmentList: dueAssignmentList.data,
          socialAccounts: socialAccounts.data || [],
          refetchSocialAccounts: socialAccounts.refetch,
          offerLetter: offerLettersQuery,
          listGrowthspaceQuery,
          growthspaces: growthspaces ?? [],
          currentGrowthspace,
          switchGrowthspace,
          isGrowthSpaceUser,
          isNcte,
        }}
      >
        <ScrollRestoration />
        <FeatureToggleManagerProvider>
          <ResumeProfileFillerProvider>
            {!reMountingApp &&
              (showDownloadPrompt ? <SuraasaSureDownloadPrompt /> : <Outlet />)}
          </ResumeProfileFillerProvider>
        </FeatureToggleManagerProvider>
      </context.Provider>
    </ErrorBoundary>
  )
}

export default GlobalContext
